import React, { lazy, Suspense } from "react";
import LiveLocationTrackingImage1 from "@/assets/LiveLocationTracking/waggex-field-team-productivity-live-location-monitoring600.webp?w=350;400&format=webp";
import LiveLocationTrackingImage2 from "@/assets/LiveLocationTracking/waggex-gps-attendance-live-location-linked-india800.webp?w=350;400&format=webp";
import LiveLocationTrackingImage3 from "@/assets/LiveLocationTracking/waggex-live-map-employee-location-tracking-dashboard600.webp?w=380;450&format=webp";
// import LiveLocationTrackingImage4 from "@/assets/LiveLocationTracking/geo-tagged-attendance-payroll-sync.webp?w=400;600&format=webp";

import { useNavigate } from "react-router-dom";
import mobile364 from "@/assets/LiveLocationTracking/waggex-employee-location-tracking-software-india364.webp";
import mobile556 from "@/assets/LiveLocationTracking/waggex-employee-location-tracking-software-india556.webp";

import StaffAppFeatures from "./staffapp/StaffAppFeatures";
import StatsSection from "./common/StatsSection";

const CustomerReviews = lazy(
  () => import("../components/payrolewebapp/CustomerReviews"),
);

const HomeFaq = lazy(() => import("../pages/HomeFaq"));

const AppDownloadCard = lazy(
  () => import("../components/common/AppDownloadCard"),
);

const PayrollBanner = lazy(() => import("../components/common/PayrollBanner"));

const ModernizeHR = lazy(() => import("../components/common/ModernizeHR"));
import {
  Route,
  Monitor,
  MapPinned,
  Check,
  MapPinCheckInside,
  MonitorSmartphone,
  BellRing,
  HistoryIcon,
} from "lucide-react";
import ThreeSteps from "./ThreeSteps";
import { Helmet } from "react-helmet-async";
import { useHrmsCounts } from "../hooks/useHrmsCounts";
import { useInView } from "react-intersection-observer";
const features = [
  "Sales & Marketing Teams",
  "Delivery & Logistics Companies",
  "Construction & Site Management",
  "Service & Maintenance Businesses",
  "Healthcare Field Staff",
  "Facility Management Teams",
  "FMCG Distribution Teams",
  "Security & Patrol Services",
];

const liveLocationTrackingFaqs1 = [
  {
    title: "How does employee live location tracking work in Waggex?",
    content:
      "Waggex uses GPS technology on employee mobile devices to capture accurate live location updates, movement history, and route data throughout the working day. All location information is transmitted to the manager dashboard in real time.",
  },
  {
    title:
      "Can employees mark GPS-based attendance with location verification?",
    content:
      "Yes. Employees can mark attendance directly from the Waggex app with GPS coordinates captured at the point of check-in. This ensures attendance is marked from the correct work location and helps prevent fake or remote check-ins.",
  },
  {
    title: "Is complete route history available for each employee?",
    content:
      "Yes. Managers can view complete travel routes, stop durations, movement timelines, and location history for each field employee. Route data is stored with timestamps and can be accessed anytime from the central dashboard.",
  },
];

const liveLocationTrackingFaqs2 = [
  {
    title: "Does Waggex employee tracking work for field and remote teams?",
    content:
      "Yes. Waggex is designed for field-based operations including sales teams, service engineers, delivery executives, construction staff, and remote employees working outside a fixed office location.",
  },
  {
    title: "Can multiple field employees be monitored simultaneously?",
    content:
      "Yes. The Waggex dashboard allows managers to monitor all active field employees on a live map simultaneously. Managers can track movement status, visit updates, task progress, and team activity from a single dashboard.",
  },
  {
    title:
      "Does Waggex employee location tracking work across all cities in India?",
    content:
      "Yes. Waggex works across all cities, districts, and regions in India using mobile GPS technology. As long as GPS and internet connectivity are available on the employee device, location tracking remains active and accurate.",
  },
];

const trackingFeatures = [
  {
    icon: <MapPinned size={24} aria-hidden="true" />,
    title: "Real-Time GPS Tracking",
    desc: "Access employee live location from their mobile device with accurate GPS updates and complete movement history.",
  },
  {
    icon: <MapPinCheckInside size={24} aria-hidden="true" />,
    title: "Geo-Tagged Attendance",
    desc: "Verify that employees mark attendance from the correct job site using GPS coordinates captured at every check-in.",
  },
  {
    icon: <Route size={24} aria-hidden="true" />,
    title: "Route Monitoring",
    desc: "Monitor travel routes, stop duration, and employee movement throughout the working day for full field visibility.",
  },
  {
    icon: <HistoryIcon size={24} aria-hidden="true" />,
    title: "Route History",
    desc: "Access previous travel records, movement timelines, and employee location history for better workforce tracking.",
  },
  {
    icon: <MonitorSmartphone size={24} aria-hidden="true" />,
    title: "Remote Team Monitoring",
    desc: "Track remote teams with live location, attendance, and real-time work activity updates from a centralized dashboard.",
  },
  {
    icon: <BellRing size={24} aria-hidden="true" />,
    title: "Instant Alerts & Notifications",
    desc: "Receive instant alerts for late check-ins, inactive employees, route deviations, or missed client field visits.",
  },
];
const trackingProblems = [
  {
    number: "1",
    label: "",
    title: "NO REAL-TIME VISIBILITY",
    description:
      "Without live GPS tracking, businesses cannot confirm where employees are working or whether assigned client visits are actually being completed.",
  },
  {
    number: "2",
    label: "",
    title: "DELAYED REPORTING",
    description:
      "Relying on phone calls and manual status updates delays decision-making, reduces response time, and affects overall field team productivity.",
  },
  {
    number: "3",
    label: "",
    title: "DIFFICULT WORKFORCE MANAGEMENT",
    description:
      "Coordinating field employees without GPS-based monitoring creates operational confusion, inefficient task allocation, and poor workforce visibility.",
  },
];
const steps = [
  {
    title: "Increase Accountability",
    desc: "Employees work more responsibly when movement, location, and daily activity are tracked accurately in real time.",
  },
  {
    title: "Reduce Operational Costs",
    desc: "Optimize travel routes, reduce unnecessary movement, and improve overall field workforce efficiency.",
  },
  {
    title: "Improve Customer Service",
    desc: "Assign the nearest available employee to urgent requests using live employee location tracking data.",
  },
  {
    title: "Easy to Use Mobile App",
    desc: "Works on any Android device with a simple and user-friendly interface designed for field employees.",
  },
];
const schema = [
  // SCHEMA 1: SoftwareApplication
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Waggex Employee Location Tracking",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Android, iOS, Web",
    description:
      "Waggex provides real-time GPS employee location tracking for field teams in India — live map view, route monitoring, geo-tagged attendance, visit verification, and payroll integration.",
    url: "https://www.waggex.com/employee-location-tracking-software",

    screenshot:
      "https://www.waggex.com/waggex-field-team-productivity-live-location-monitoring.webp",
    featureList: [
      "Employee location tracking India",
      "Live GPS tracking for field staff",
      "Real-time employee location monitoring",
      "Geo-tagged attendance verification",
      "Route monitoring and visit verification",
      "Field employee tracking app India",
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
      description: "Free trial available",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "289",
      bestRating: "5",
      worstRating: "1",
    },
  },

  // SCHEMA 2: FAQPage
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How does employee live location tracking work in Waggex?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Waggex uses GPS on employee devices to capture real-time location, movement history, and route data throughout the working day, visible live on the manager dashboard.",
        },
      },
      {
        "@type": "Question",
        name: "Can employees mark GPS-based attendance with location verification?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Every attendance check-in is GPS-tagged to ensure employees are at the correct work location when marking attendance.",
        },
      },
      {
        "@type": "Question",
        name: "Is complete route history available for each employee?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Managers can view full route history, timestamps, and movement logs for each field employee.",
        },
      },
      {
        "@type": "Question",
        name: "Does Waggex employee tracking work for field and remote teams?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. It is designed for field staff, sales teams, delivery executives, and remote workers across India.",
        },
      },
      {
        "@type": "Question",
        name: "Can multiple field employees be monitored simultaneously?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Managers can track all employees live on a single dashboard map in real time.",
        },
      },
      {
        "@type": "Question",
        name: "Does Waggex employee location tracking work across all cities in India?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. It works nationwide wherever GPS is available on the employee device.",
        },
      },
    ],
  },

  // SCHEMA 3: Organization
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides employee location tracking and complete HRMS for Indian businesses including GPS tracking, attendance, payroll automation, and field team management.",
    areaServed: "IN",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
  },
];
export default function LiveLocationTracking() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { counts, loading } = useHrmsCounts(inView);
  const handleRedirect = () => {
    window.location.href = "https://app.waggex.com/signup";
  };

  return (
    <>
      {/* <SEO
        title="Live Location Tracking Software | GPS Field Staff Monitoring | Waggex"
        description="Track field employees in real time with Waggex Live Location Tracking. Use GPS attendance, geo-fencing, and anti-mock technology to improve productivity and eliminate attendance fraud"
        keywords="Live location tracking, Field staff tracking, GPS attendance system, Geo-fencing for business, Real-time workforce monitoring"
        canonical="https://www.waggex.com /employee-location-tracking-software"
      /> */}
      <Helmet>
        <title>Employee Location Tracking Software | GPS Tracking App</title>
        <meta
          name="description"
          content="Track field employees in real time with Waggex Employee Location Tracking Software. Monitor GPS locations, attendance, routes, and field visits."
        />
        <meta
          name="keywords"
          content="employee location tracking,live location tracking India,GPS employee tracking software India,field employee tracking app,real-time employee location tracking,employee tracking software India,live GPS tracking for field staff,employee location monitoring India,field workforce tracking software"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/employee-location-tracking-software"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <section className="w-full bg-[#d1dfea] overflow-hidden py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-10">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-block border border-[#10324F] rounded-xl px-5 py-2 text-[#10324F] text-xs sm:text-sm font-medium">
                Employee Location Tracking
              </div>

              {/* Heading */}
              <h1 className="mt-8 text-[#10324F] font-bold uppercase tracking-wide text-xl md:text-[28px] min-h-16">
                Employee Location Tracking Software With Live GPS Tracking
              </h1>

              {/* Description */}
              <p className="service-list-item-text py-4 max-w-xl">
                Track employee locations in real time with Waggex Employee
                Location Tracking Software. Monitor GPS locations, verify work
                visits, review travel history, and manage attendance from a
                single dashboard. Whether your team works in the office,
                remotely, or on the move, Waggex provides complete visibility to
                help improve productivity and accountability
              </p>
              <h2 className="text-[#10324F] font-roboto font-bold text-sm">
                Track You Team Across India
              </h2>

              {/* Description */}
              <p className="service-list-item-text-2 py-4 max-w-xl">
                Whether your team operates in sales, service, delivery,
                construction, or field operations, Waggex gives complete
                visibility into employee live locations, travel routes, and
                daily work activity in real time. Remove manual follow-ups,
                eliminate fake attendance reporting, and manage field teams
                using accurate live location intelligence.
              </p>

              {/* Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() =>
                    (window.location.href = "https://app.waggex.com/signup")
                  }
                  className="w-full sm:w-auto bg-[#10324F] hover:bg-[#10324F] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
                >
                  Start Free Trial
                </button>

                <button
                  onClick={() => (window.location.href = "/contact-us")}
                  className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
                >
                  Book a Demo
                </button>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative flex justify-center items-center">
              {/* Phones Image */}
              {/* <img
                src={mobile364}
                width={382}
                height={296}
                fetchPriority="high"
                loading="eager"
                alt="Waggex real-time employee location tracking software dashboard showing live GPS positions of field team across India"
                className="relative object-contain block sm:hidden"
              /> */}
              <picture>
                <source media="(max-width: 639px)" srcSet={mobile364} />
                <img
                  src={mobile556}
                  fetchPriority="high"
                  width={556}
                  height={430}
                  loading="eager"
                  decoding="async"
                  alt="Waggex real-time employee location tracking software dashboard showing live GPS positions of field team across India"
                  className="relative object-contain"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>
      <section ref={ref}>
        <StatsSection counts={counts} loading={loading} />
      </section>
      <StaffAppFeatures
        title="Track Employee Locations in Real Time With GPS"
        subtitle="Manage employee attendance, location tracking, productivity, and daily operations from one unified platform"
        features={trackingFeatures}
      />

      <section className=" bg-[#D1DFEA33] backdrop-blur-[30px]  px-6 py-10">
        <div className="max-w-6xl mx-auto ">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            {/* LEFT — Feature List */}
            <div className="payroll-section-content-width">
              <h2 className="service-section-heading mb-3">
                Improve Field Team Productivity With Live Location Monitoring
              </h2>

              <div className="space-y-4 mt-4">
                {/* Item 1 */}
                <div className="flex gap-4 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <div>
                    <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                      Accurate Location Visibility
                    </h2>
                    <p className="service-list-item-text mt-1">
                      View employee movement and work progress in real time
                      without constant phone calls or manual check-ins
                    </p>
                  </div>
                </div>

                {/* Item 2 */}
                <div className="flex gap-4 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <div>
                    <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                      Automated Reporting
                    </h2>
                    <p className="service-list-item-text mt-1">
                      Generate attendance, travel history, work hours, and field
                      activity reports automatically with minimal effort.
                    </p>
                  </div>
                </div>

                {/* Item 3 */}
                <div className="flex gap-4 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <div>
                    <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                      Better Team Coordination
                    </h2>
                    <p className="service-list-item-text mt-1">
                      Assign the nearest available employee to urgent field
                      tasks faster using real-time employee location data
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center md:justify-center">
              <img
                src={LiveLocationTrackingImage1[1]}
                width="448"
                height="329"
                fetchPriority="high"
                alt="Waggex improving field team productivity with live location monitoring and automated reporting for Indian businesses"
                className="object-contain hidden sm:block"
              />
              <img
                src={LiveLocationTrackingImage1[0]}
                width="490"
                height="359"
                fetchPriority="high"
                alt="Waggex improving field team productivity with live location monitoring and automated reporting for Indian businesses"
                className="object-contain block sm:hidden"
              />
            </div>

            {/* RIGHT — Image */}
          </div>
        </div>
      </section>
      <section className=" bg-white backdrop-blur-[30px]  px-6 py-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            {/* LEFT — Feature List */}

            <div className="flex justify-center md:justify-center">
              {/* <img
                src={LiveLocationTrackingImage3} // your illustration image */}
              <img
                src={LiveLocationTrackingImage2[0]} // fallback image
                // srcSet={`${LiveLocationTrackingImage2[0]} 400w,${LiveLocationTrackingImage2[1]} 600w`}
                sizes="(max-width: 640px) 100vw"
                width="382"
                height="382"
                fetchPriority="high"
                alt="Waggex GPS-based employee attendance linked with live location tracking preventing fake check-ins India"
                className="object-contain block sm:hidden"
              />
              <img
                src={LiveLocationTrackingImage2[1]} // fallback image
                // srcSet={`${LiveLocationTrackingImage2[0]} 400w,${LiveLocationTrackingImage2[1]} 600w`}
                sizes="(max-width: 640px) 100vw"
                width="448"
                height="448"
                fetchPriority="high"
                alt="Waggex GPS-based employee attendance linked with live location tracking preventing fake check-ins India"
                className="object-contain hidden sm:block"
              />
            </div>

            <div className="payroll-section-content-width">
              <h2 className="service-section-heading">
                GPS Attendance Linked With Live Location
              </h2>

              <p className="service-list-item-text py-4 max-w-md sm:max-w-2xl mx-auto">
                Manage field attendance and live employee location tracking
                together in one unified platform
              </p>

              <ul className="space-y-3 mt-3">
                <li className="flex gap-3 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <span className="service-list-item-text">
                    GPS-verified employee attendance at every check-in
                  </span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <span className="service-list-item-text">
                    Prevent fake check-ins and buddy punching with location
                    verification
                  </span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <span className="service-list-item-text">
                    Real-time GPS coordinates captured at the point of
                    attendance marking
                  </span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <span className="service-list-item-text">
                    Track employee working hours accurately across all field
                    locations
                  </span>
                </li>
                <li className="flex gap-3 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <span className="service-list-item-text">
                    Attendance data synchronised with payroll systems
                    automatically
                  </span>
                </li>
              </ul>
            </div>
            {/* RIGHT — Image */}
          </div>
        </div>
      </section>
      <section className=" bg-[#D1DFEA33] backdrop-blur-[30px]  px-6 py-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            {/* LEFT — Feature List */}

            <div className="payroll-section-content-width">
              <h2 className="service-section-heading">
                Precision GPS Monitoring for Field Employees
              </h2>
              <p className="service-list-item-text py-4">
                Monitor all active field employees on a centralised live map
                with accurate real-time location data and complete movement
                visibility.
              </p>
              <div className="space-y-4">
                {/* Item 1 */}
                <div className="flex gap-4 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <div>
                    <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                      Live Map View
                    </h2>
                    <p className="text-xs sm:text-sm text-slate-600 mt-1">
                      Monitor all field employees on a centralised live map
                      dashboard with real-time updates.
                    </p>
                  </div>
                </div>

                {/* Item 2 */}
                <div className="flex gap-4 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <div>
                    <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                      Location Timeline
                    </h2>
                    <p className="text-xs sm:text-sm text-slate-600 mt-1">
                      View employee travel routes, stop times, and complete
                      movement history with accurate timestamps
                    </p>
                  </div>
                </div>

                {/* Item 3 */}

                <div className="flex gap-4 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <div>
                    <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                      Activity Tracking
                    </h2>
                    <p className="text-xs sm:text-sm text-slate-600 mt-1">
                      View employee travel routes, stop times, and complete
                      movement history with accurate timestamps
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center md:justify-center">
              <img
                src={LiveLocationTrackingImage3[0]} // fallback image
                // srcSet={`${LiveLocationTrackingImage3[0]} 400w,${LiveLocationTrackingImage3[1]} 600w`}
                // sizes="(max-width: 640px) 100vw,(max-width: 1024px) 50vw,33vw"
                width="382"
                height="284"
                fetchPriority="high"
                alt="Waggex live map showing real-time employee locations and route history for field team management India"
                className="object-contain  block sm:hidden"
              />
              <img
                src={LiveLocationTrackingImage3[1]} // fallback image
                // srcSet={`${LiveLocationTrackingImage3[0]} 400w,${LiveLocationTrackingImage3[1]} 600w`}
                // sizes="(max-width: 640px) 100vw,(max-width: 1024px) 50vw,33vw"
                width="450"
                height="334"
                fetchPriority="high"
                alt="Waggex live map showing real-time employee locations and route history for field team management India"
                className="object-contain hidden sm:block"
              />
            </div>
          </div>
        </div>
      </section>
      <ThreeSteps
        title="Your Business Needs Employee Location Tracking Software"
        subtitle="Without Employee Location Tracking Software, managing employees becomes time-consuming. Limited visibility, manual follow-ups, and delayed updates can reduce productivity and impact daily operations"
        steps={trackingProblems}
        backcolor="#FFFFFF"
      />
      <section className="w-full bg-[#F6F9FB]  py-10 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Heading */}
          <h2 className="text-center text-[#10324F] text-xl md:text-3xl font-bold uppercase tracking-wide">
            Industries Using Employee Live Location Tracking
          </h2>
          <p className="font-roboto font-normal text-base leading-6 text-center text-[#10324F] mt-4 pb-8">
            Waggex employee location tracking is deployed across a wide range of
            field-based industries across India
          </p>
          {/* Features Grid */}
          <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-10">
            {features.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-3 text-[#10324F]"
              >
                {/* Diamond Icon */}
                <div className="shrink-0 w-5 h-5 mt-1 flex items-center justify-center">
                  <div className="diamond-icon-sm"></div>
                </div>

                {/* Text */}
                <p className="text-sm md:text-base font-medium leading-relaxed text-[#10324F]">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="w-full bg-[#FFFFFF]  py-10  px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto mt-4 mb-4">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl sm:text-3xl font-bold uppercase">
              How It Works
            </h2>

            <p className="mt-4 font-roboto font-normal text-base leading-6 text-center text-[#10324F]">
              Most Indian businesses still rely on WhatsApp check-ins and manual
              registers. Here is how Waggex changes tha
            </p>
          </div>

          {/* Steps */}
          <div className="mt-14 flex flex-col lg:flex-row items-center justify-center gap-5">
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                {/* Card */}
                <div
                  className="
                        w-full
                        sm:w-66
                        bg-white
                        rounded-2xl
                        p-6
                        transition-all
                        duration-300
                        min-h-38
                        flex flex-col justify-start
                      "
                  style={{
                    boxShadow: `
            rgb(167 191 210 / 40%) -15px -15px 20px inset,
            rgb(170 188 202 / 30%) -20px -20px 30px
          `,
                  }}
                >
                  {/* Title */}
                  <h2 className="mt-2 text-[#10324F] text-lg font-semibold">
                    {step.title}
                  </h2>

                  {/* Description */}
                  <p className="mt-3 service-list-item-text">{step.desc}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>
      {/* reviews */}
      <Suspense fallback={null}>
        <CustomerReviews />
        <HomeFaq
          faq1={liveLocationTrackingFaqs1}
          faq2={liveLocationTrackingFaqs2}
          subtitle="Everything you need to know about Waggex payroll software in India"
        />
        {/*  <AppDownloadCard /> */}
        <PayrollBanner />
        <AppDownloadCard />
        <ModernizeHR
          heading="Manage Your Field Employees Smarter With Waggex"
          description="Track employee live locations, monitor field attendance, and improve workforce productivity from one powerful platform built for modern Indian businesses."
        />
      </Suspense>
    </>
  );
}
